import {
  OutboxEventStatus,
  enumOutboxEventStatus,
} from '@rematter/frontend-lib-api-client';
import {
  Col,
  Row,
  Loading,
  Badge,
  cn,
  humanReadableDateTime,
  DateRangePicker,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideChevronLeft, LucideChevronRight } from 'lucide-react';

import { FilesOutboxEventRecord } from '@/audit-log/_graphql/queryFilesOutboxEvents';
import {
  $remoteFilesOutboxEvents,
  useSelectedFilesEventId,
  useFilesPagination,
  useFilesDateRangeFilter,
} from '@/audit-log/_state/filesEvents.state';
import { useTranslations } from '@/audit-log/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: OutboxEventStatus) => {
  switch (status) {
    case enumOutboxEventStatus.Processed:
      return 'success';
    case enumOutboxEventStatus.Pending:
      return 'warning';
    case enumOutboxEventStatus.Failed:
      return 'failure';
    case enumOutboxEventStatus.DeadLetter:
      return 'failure';
    default:
      return 'neutral';
  }
};

type FileEventTypeTranslations = {
  file: {
    created: string;
    accessed: string;
    deleted: string;
    quotaExceeded: string;
    moved: string;
    permissionChanged: string;
    setAsTemplate: string;
    removedFromTemplate: string;
  };
};

const getEventTypeKey = (
  eventName: string,
  payload: string,
): {
  aggregate: keyof FileEventTypeTranslations;
  action:
    | 'created'
    | 'accessed'
    | 'deleted'
    | 'quotaExceeded'
    | 'moved'
    | 'permissionChanged'
    | 'setAsTemplate'
    | 'removedFromTemplate';
} | null => {
  try {
    const parsed = JSON.parse(payload);

    if (eventName !== 'FileEvent') return null;

    type ActionKey =
      | 'created'
      | 'accessed'
      | 'deleted'
      | 'quotaExceeded'
      | 'moved'
      | 'permissionChanged'
      | 'setAsTemplate'
      | 'removedFromTemplate';

    let action: ActionKey | null = null;
    if (parsed.created) action = 'created';
    else if (parsed.accessed) action = 'accessed';
    else if (parsed.deleted) action = 'deleted';
    else if (parsed.quotaExceeded) action = 'quotaExceeded';
    else if (parsed.moved) action = 'moved';
    else if (parsed.permissionChanged) action = 'permissionChanged';
    else if (parsed.setAsTemplate) action = 'setAsTemplate';
    else if (parsed.removedFromTemplate) action = 'removedFromTemplate';

    if (!action) return null;

    return { aggregate: 'file', action };
  } catch {
    return null;
  }
};

const getTranslatedEventName = (
  eventName: string,
  payload: string,
  eventTypes: FileEventTypeTranslations,
): string => {
  const key = getEventTypeKey(eventName, payload);
  if (key) {
    return eventTypes[key.aggregate][key.action];
  }
  return eventName;
};

type EventCardProps = {
  event: FilesOutboxEventRecord;
  isSelected: boolean;
  onClick: () => void;
  idPrefix: string;
  notAvailable: string;
  eventTypes: FileEventTypeTranslations;
};

const EventCard = ({
  event,
  isSelected,
  onClick,
  idPrefix,
  notAvailable,
  eventTypes,
}: EventCardProps) => (
  <button
    onClick={onClick}
    className={cn(
      '@EventCard',
      cn(
        'w-full rounded-md border p-3 text-left transition-colors',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-neutral/10 hover:border-neutral/20 hover:bg-neutral/5',
      ),
    )}
  >
    <Col gap2>
      <Row className="justify-between">
        <span className="text-sm font-medium">
          {getTranslatedEventName(event.eventName, event.payload, eventTypes)}
        </span>
        <Badge
          color={getStatusColor(event.status as OutboxEventStatus)}
          size="small"
        >
          {event.status}
        </Badge>
      </Row>
      <Row className="text-xs text-neutral/60" gap2>
        <span>
          {idPrefix} {event.metadata.aggregateId ?? notAvailable}
        </span>
      </Row>
      <Row className="text-xs text-neutral/40">
        <span>
          {humanReadableDateTime(event.timestamps.createdAt, {
            locale: getLocale(),
            includeYear: true,
            includeSeconds: true,
          })}
        </span>
      </Row>
    </Col>
  </button>
);

export const FilesEventsList = () => {
  const t = useTranslations();
  const [pagination, setPagination] = useFilesPagination();
  const [selectedEventId, setSelectedEventId] = useSelectedFilesEventId();
  const [dateRange, setDateRange] = useFilesDateRangeFilter();

  const { data, isLoading } = useAtomValue($remoteFilesOutboxEvents);

  const events = data?.events ?? [];
  const total = data?.total ?? 0;

  const hasNextPage = pagination.offset + pagination.limit < total;
  const hasPrevPage = pagination.offset > 0;

  const goToNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const goToPrevPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  if (isLoading && !data) {
    return (
      <Col className="@FilesEventsList flex-1 items-center justify-center border-r border-neutral/10">
        <Loading isLoading />
      </Col>
    );
  }

  return (
    <Col className="@FilesEventsList w-[350px] border-r border-neutral/10">
      <Row className="border-b border-neutral/10 p-3">
        <Col className="flex-1">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            aria-label={t.filters.dateRange.label}
            clearable
            onClear={() => setDateRange(null)}
          />
        </Col>
      </Row>
      <Col className="flex-1 gap-2 overflow-y-auto p-3">
        {events.length === 0 ? (
          <Col className="flex-1 items-center justify-center text-neutral/40">
            <span>{t.filesEventsView.list.empty}</span>
          </Col>
        ) : (
          events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              isSelected={selectedEventId === event.id}
              onClick={() => setSelectedEventId(event.id)}
              idPrefix={t.filesEventsView.list.idPrefix}
              notAvailable={t.filesEventsView.list.notAvailable}
              eventTypes={t.filesEventsView.detail.eventTypes}
            />
          ))
        )}
      </Col>

      <Row className="items-center justify-between border-t border-neutral/10 px-3 py-2">
        <span className="text-xs text-neutral/60">
          {pagination.offset + 1} -{' '}
          {Math.min(pagination.offset + pagination.limit, total)} of {total}
        </span>
        <Row gap1>
          <button
            onClick={goToPrevPage}
            disabled={!hasPrevPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasPrevPage
                ? 'hover:bg-neutral/10'
                : 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronLeft size={16} />
          </button>
          <button
            onClick={goToNextPage}
            disabled={!hasNextPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasNextPage
                ? 'hover:bg-neutral/10'
                : 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronRight size={16} />
          </button>
        </Row>
      </Row>
    </Col>
  );
};
